import { useCallback, useMemo, useRef, useState } from 'react';

import { CellValueChangedEvent } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/app/hooks';
import {
  FertWorkDetailRecord,
  FertWorkValidationIssue as FertWorkValidationIssueType,
  useDeleteSessionDetailMutation,
  useEscalateIssuesMutation,
  useResolveEscalationMutation,
  useSplitSessionDetailMutation,
  useUpdateSessionDetailMutation,
} from '@/app/services/fertWork';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { isBlockingIssue } from '@/pages/fert-work/utils/statusHelpers';
import { hasPermission, MODULES } from '@/services/auth';
import { useToast } from '@/services/toast';

import { AlertPanel, AlertPills, AlertTabType } from '../AlertsTabbedSection';
import { EditDonorModal } from '../EditDonorModal';
import { FixIssuesModal } from '../FixIssuesModal';
import { RemoveDonorModal } from '../RemoveDonorModal';
import { ValidationGrid } from '../ValidationGrid';
import { ViewDonorModal } from '../ViewDonorModal';

const abbreviateBreed = (breed: string | null | undefined): string => {
  if (!breed) return '';
  const breedMap: Record<string, string> = {
    Holstein: 'HO',
    Jersey: 'JE',
    Angus: 'AN',
  };
  return breedMap[breed] || breed;
};

export type SessionTabProps = {
  sessionId: string;
  sessionDetail: any;
};

export const SessionTab = ({ sessionId, sessionDetail }: SessionTabProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { me } = useAppSelector((state) => state.authentication);
  const hasWritePermission = hasPermission(me, MODULES.FERT_WORK_WRITE);

  const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
  const [selectedWarnings, setSelectedWarnings] = useState<string[]>([]);
  const [showFixIssuesModal, setShowFixIssuesModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDonorRecord, setSelectedDonorRecord] =
    useState<FertWorkDetailRecord | null>(null);
  const [showRules, setShowRules] = useState(false);
  const [activeAlertTab, setActiveAlertTab] = useState<AlertTabType>(null);
  const pendingEditsRef = useRef<Record<string, Partial<FertWorkDetailRecord>>>(
    {},
  );

  const [updateSessionDetail] = useUpdateSessionDetailMutation();
  const [deleteSessionDetail] = useDeleteSessionDetailMutation();
  const [splitSessionDetail] = useSplitSessionDetailMutation();
  const [escalateIssues] = useEscalateIssuesMutation();
  const [resolveEscalation] = useResolveEscalationMutation();

  const handleEditDonor = (donorId: string) => {
    const record = sessionDetail?.details.find(
      (d: FertWorkDetailRecord) => d.id === donorId,
    );
    if (record) {
      const merged = { ...record, ...pendingEditsRef.current[donorId] };
      setSelectedDonorRecord(merged);
      setShowEditModal(true);
    }
  };

  const handleRemoveDonor = (donorId: string) => {
    const record = sessionDetail?.details.find(
      (d: FertWorkDetailRecord) => d.id === donorId,
    );
    if (record) {
      setSelectedDonorRecord(record);
      setShowRemoveModal(true);
    }
  };

  const handleSaveDonor = async (updatedRecord: FertWorkDetailRecord) => {
    try {
      await updateSessionDetail({
        sessionId,
        detailId: updatedRecord.id,
        data: updatedRecord,
      }).unwrap();
    } catch (error) {
      console.error('Failed to update donor record:', error);
      toast.show({
        severity: 'error',
        summary: t('fertWork.toasts.updateFailed', 'Update Failed'),
        detail: t(
          'fertWork.toasts.updateFailedDetail',
          'Failed to update donor record. Please try again.',
        ),
        life: 5000,
      });
    }
  };

  const handleCellValueChanged = useCallback(
    async (event: CellValueChangedEvent) => {
      const { data, colDef, newValue } = event;
      const record = sessionDetail?.details.find(
        (d: FertWorkDetailRecord) => d.id === data.id,
      );
      if (!record || !colDef.field) return;

      pendingEditsRef.current[record.id] = {
        ...pendingEditsRef.current[record.id],
        [colDef.field]: newValue,
      };

      const updatedRecord = {
        ...record,
        ...pendingEditsRef.current[record.id],
      };
      try {
        await updateSessionDetail({
          sessionId,
          detailId: record.id,
          data: updatedRecord,
        }).unwrap();
        delete pendingEditsRef.current[record.id];
      } catch (error) {
        console.error('Failed to update inline edit:', error);
        toast.show({
          severity: 'error',
          summary: t('fertWork.toasts.updateFailed', 'Update Failed'),
          detail: t(
            'fertWork.toasts.updateFailedDetail',
            'Failed to update donor record. Please try again.',
          ),
          life: 5000,
        });
      }
    },
    [sessionId, sessionDetail?.details, updateSessionDetail, t],
  );

  const handleConfirmRemove = async (donorId: string) => {
    try {
      await deleteSessionDetail({
        sessionId,
        detailId: donorId,
      }).unwrap();
    } catch (error) {
      console.error('Failed to delete donor record:', error);
      toast.show({
        severity: 'error',
        summary: t('fertWork.toasts.deleteRecordFailed', 'Delete Failed'),
        detail: t(
          'fertWork.toasts.deleteRecordFailedDetail',
          'Failed to remove donor record. Please try again.',
        ),
        life: 5000,
      });
    }
  };

  const handleFixSelectedIssues = () => {
    setShowFixIssuesModal(true);
  };

  const handleFixSelectedWarnings = () => {
    setShowFixIssuesModal(true);
  };

  const handleSplitDonor = async (splitId: string) => {
    const split = sessionDetail?.analytics?.possibleSplits.find(
      (s: any) => s.detailId === splitId,
    );
    if (split) {
      try {
        await splitSessionDetail({
          sessionId,
          detailId: split.detailId,
        }).unwrap();
        toast.show({
          severity: 'success',
          summary: t('fertWork.toasts.splitSuccessful', 'Split Successful'),
          detail: t(
            'fertWork.toasts.splitSuccessfulDetail',
            'Donor {name} has been split successfully.',
            {
              name: split.donorName,
            },
          ),
          life: 5000,
        });
      } catch (error) {
        console.error('Failed to split donor:', error);
        toast.show({
          severity: 'error',
          summary: t('fertWork.toasts.splitFailed', 'Split Failed'),
          detail: t(
            'fertWork.toasts.splitFailedDetail',
            'Failed to split donor. Please try again.',
          ),
          life: 5000,
        });
      }
    }
  };

  const handleViewRevision = (revisionId: string) => {
    const record = sessionDetail?.details.find(
      (d: FertWorkDetailRecord) => d.id === revisionId,
    );
    if (record) {
      setSelectedDonorRecord(record);
      setShowViewModal(true);
    }
  };

  const handleViewIssue = (issueId: string) => {
    if (!selectedIssues.includes(issueId)) {
      setSelectedIssues([issueId]);
    }
    setShowFixIssuesModal(true);
  };

  const handleFixWarning = (warningId: string) => {
    if (!selectedWarnings.includes(warningId)) {
      setSelectedWarnings([warningId]);
    }
    setShowFixIssuesModal(true);
  };

  const findRealIssueId = (compositeId: string): string | null => {
    if (!sessionDetail?.issues) return null;
    // compositeId is like "DonorName-IssueType-0" — match by donor + issueType
    const parts = compositeId.split('-');
    if (parts.length < 3) return null;
    const index = parseInt(parts[parts.length - 1]);
    const issueType = parts[parts.length - 2];
    const donorName = parts.slice(0, parts.length - 2).join('-');

    const matchingIssues = (
      sessionDetail.issues as FertWorkValidationIssueType[]
    ).filter((i) => {
      const detail = sessionDetail.details.find(
        (d: FertWorkDetailRecord) => d.id === i.sessionDetailId,
      );
      return (
        detail?.donorName === donorName &&
        i.issueType === issueType &&
        !i.isResolved
      );
    });

    return matchingIssues[index]?.id ?? matchingIssues[0]?.id ?? null;
  };

  const handleEscalateIssue = async (issueId: string) => {
    const realId = findRealIssueId(issueId);
    if (!realId) return;
    try {
      await escalateIssues({ sessionId, issueIds: [realId] }).unwrap();
      toast.show({
        severity: 'warn',
        summary: t('fertWork.toasts.issueEscalated', 'Issue Escalated'),
        detail: t(
          'fertWork.toasts.issueEscalatedDetail',
          'Issue has been escalated for confirmation.',
        ),
        life: 5000,
      });
    } catch (error) {
      console.error('Failed to escalate issue:', error);
      toast.show({
        severity: 'error',
        summary: t('fertWork.toasts.escalationFailed', 'Escalation Failed'),
        detail: t(
          'fertWork.toasts.escalationFailedDetail',
          'Failed to escalate issue. Please try again.',
        ),
        life: 5000,
      });
    }
  };

  const handleResolveEscalation = async (issueId: string) => {
    const realId = findRealIssueId(issueId);
    if (!realId) return;
    try {
      await resolveEscalation({ sessionId, issueIds: [realId] }).unwrap();
      toast.show({
        severity: 'success',
        summary: t('fertWork.toasts.escalationResolved', 'Escalation Resolved'),
        detail: t(
          'fertWork.toasts.escalationResolvedDetail',
          'Escalation has been resolved.',
        ),
        life: 5000,
      });
    } catch (error) {
      console.error('Failed to resolve escalation:', error);
      toast.show({
        severity: 'error',
        summary: t('fertWork.toasts.resolutionFailed', 'Resolution Failed'),
        detail: t(
          'fertWork.toasts.resolutionFailedDetail',
          'Failed to resolve escalation. Please try again.',
        ),
        life: 5000,
      });
    }
  };

  const { issues, warnings } = useMemo(() => {
    if (!sessionDetail?.analytics?.issuesByDonor)
      return { issues: [], warnings: [] };

    const realIssuesByDonorAndType: Record<
      string,
      FertWorkValidationIssueType[]
    > = {};
    if (sessionDetail?.issues) {
      (sessionDetail.issues as FertWorkValidationIssueType[]).forEach(
        (issue) => {
          if (issue.isResolved) return;
          const detail = sessionDetail.details.find(
            (d: FertWorkDetailRecord) => d.id === issue.sessionDetailId,
          );
          const key = `${detail?.donorName}-${issue.issueType}`;
          if (!realIssuesByDonorAndType[key])
            realIssuesByDonorAndType[key] = [];
          realIssuesByDonorAndType[key].push(issue);
        },
      );
    }

    const allIssues = sessionDetail.analytics.issuesByDonor.flatMap(
      (donorIssue: any) =>
        donorIssue.issueTypes.map((issueType: string, index: number) => {
          const key = `${donorIssue.donorName}-${issueType}`;
          const realIssue =
            realIssuesByDonorAndType[key]?.[index] ??
            realIssuesByDonorAndType[key]?.[0];

          return {
            id: `${donorIssue.donorName}-${issueType}-${index}`,
            donor: donorIssue.donorName,
            message: issueType.replace(/([A-Z])/g, ' $1').trim(),
            issueType,
            isBlocking: isBlockingIssue(issueType),
            isEscalated: realIssue?.isEscalated ?? false,
            escalatedBy: realIssue?.escalatedBy,
            escalationNote: realIssue?.escalationNote,
          };
        }),
    );

    return {
      issues: allIssues.filter((issue: any) => issue.isBlocking),
      warnings: allIssues.filter((issue: any) => !issue.isBlocking),
    };
  }, [
    sessionDetail?.analytics?.issuesByDonor,
    sessionDetail?.issues,
    sessionDetail?.details,
  ]);

  const splits = useMemo(() => {
    if (!sessionDetail?.analytics?.possibleSplits) return [];

    const isClientRequestedSplit = (donorName: string): boolean => {
      if (!donorName) return false;
      return (
        donorName.includes('(1/2)') ||
        donorName.includes('(1/3)') ||
        donorName.includes('(1/4)') ||
        donorName.includes('(2/2)') ||
        donorName.includes('(2/3)') ||
        donorName.includes('(3/3)') ||
        donorName.includes('(2/4)') ||
        donorName.includes('(3/4)') ||
        donorName.includes('(4/4)')
      );
    };

    return sessionDetail.analytics.possibleSplits
      .filter((split: any) => !isClientRequestedSplit(split.donorName))
      .map((split: any) => ({
        id: split.detailId,
        donor: split.donorName,
        oocytes: split.totalOocytes,
        splitRatio: split.splitRatio,
      }));
  }, [sessionDetail?.analytics?.possibleSplits]);

  const revisions = useMemo(() => {
    if (!sessionDetail?.analytics?.revisionsAvailable) return [];

    return sessionDetail.analytics.revisionsAvailable.map((revision: any) => ({
      id: revision.detailId,
      donor: revision.donorName,
      revisionComment: revision.revisionComment,
    }));
  }, [sessionDetail?.analytics?.revisionsAvailable]);

  const dataIssues = useMemo(() => {
    const excluded = sessionDetail?.session?.excludedDonors;
    if (!excluded || excluded.length === 0) return [];
    return excluded.map((e: any, index: number) => ({
      id: `${e.donorName}-${index}`,
      donor: e.donorName,
      breed: e.breedName,
      reason: e.reason,
    }));
  }, [sessionDetail?.session?.excludedDonors]);

  const donorData = useMemo(() => {
    if (!sessionDetail?.details) return [];

    return sessionDetail.details.map((detail: FertWorkDetailRecord) => ({
      id: detail.id,
      opuSite: detail.opuSiteName || '',
      matTime: detail.matTime
        ? (() => {
            const date = new Date(detail.matTime);
            const hours = date.getUTCHours();
            const minutes = date.getUTCMinutes();
            const displayHours = hours.toString().padStart(2, '0');
            const displayMinutes = minutes.toString().padStart(2, '0');
            return `${displayHours}:${displayMinutes}`;
          })()
        : '',
      totalOocytes: detail.totalOocytes,
      splitOocytes: detail.splitOocytes ?? detail.totalOocytes,
      donorLocation: detail.donorLocation || '',
      donor: detail.donorName || '',
      donorBreed: abbreviateBreed(detail.donorBreed),
      sire: detail.sireName || '',
      sireNote: detail.sireNote || '',
      semenPullSireNote: detail.semenPullSireNote || '',
      sireBreed: abbreviateBreed(detail.sireBreed),
      collectionCode: detail.collectionCode || '',
      semenType: detail.semenType || '',
      semenComments: detail.semenComments || '',
      semenOwner: detail.semenOwnerName || '',
      semenUnits: detail.semenUnits,
      separateFert: detail.separateFert,
      account: detail.accountName || '',
      shipLabel: detail.shipLabel || '',
      exportCode: detail.exportCode || '',
      split: detail.splitRatio || '',
      revision: detail.revision || '',
      revisionComment: detail.revisionComment || '',
      hasIssues: detail.hasValidationIssues,
    }));
  }, [sessionDetail?.details]);

  return (
    <div className="flex flex-col gap-4" data-tour="session-content">
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div data-tour="alert-pills">
            <AlertPills
              issues={issues}
              revisions={revisions}
              warnings={warnings}
              dataIssues={dataIssues}
              escalatedCount={
                sessionDetail?.analytics?.escalatedIssueCount ?? 0
              }
              activeTab={activeAlertTab}
              onTabChange={setActiveAlertTab}
            />
          </div>
        </div>
        <AlertPanel
          activeTab={activeAlertTab}
          issues={issues}
          selectedIssues={selectedIssues}
          onSelectedIssuesChange={setSelectedIssues}
          onViewIssue={handleViewIssue}
          onEscalateIssue={handleEscalateIssue}
          onResolveEscalation={handleResolveEscalation}
          onFixSelectedIssues={handleFixSelectedIssues}
          revisions={revisions}
          onViewRevision={handleViewRevision}
          warnings={warnings}
          selectedWarnings={selectedWarnings}
          onSelectedWarningsChange={setSelectedWarnings}
          onFixWarning={handleFixWarning}
          onFixSelectedWarnings={handleFixSelectedWarnings}
          dataIssues={dataIssues}
        />
      </div>

      <div>
        <Button
          variant="ghost"
          size="sm"
          className="text-gray-600"
          onClick={() => setShowRules(true)}
        >
          <i className="pi pi-list" data-icon="inline-start" />
          {t('fertWork.validationRules', 'Validation Rules')}
        </Button>
      </div>

      <Dialog open={showRules} onOpenChange={setShowRules}>
        <DialogContent className="md:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t('fertWork.validationRules', 'Validation Rules')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="grid gap-4 text-sm sm:grid-cols-2 md:grid-cols-3">
              <div>
                <div className="mb-1 font-medium text-gray-700">
                  {t('fertWork.blockingIssues', 'Blocking Issues')}
                </div>
                <ul className="ml-4 list-disc space-y-0.5 text-gray-600">
                  <li>{t('fertWork.missingSire', 'Missing Sire')}</li>
                  <li>{t('fertWork.missingAccount', 'Missing Account')}</li>
                  <li>{t('fertWork.noOocytes', 'No Oocytes')}</li>
                  <li>{t('fertWork.invalidMatTime', 'Invalid Mat Time')}</li>
                </ul>
              </div>
              <div>
                <div className="mb-1 font-medium text-gray-700">
                  {t('fertWork.warnings', 'Warnings')}
                </div>
                <ul className="ml-4 list-disc space-y-0.5 text-gray-600">
                  <li>
                    {t('fertWork.missingShipLabel', 'Missing Ship Label')}
                  </li>
                </ul>
              </div>
              <div>
                <div className="mb-1 font-medium text-gray-700">
                  {t('fertWork.runLimits', 'Run Limits')}
                </div>
                <ul className="ml-4 list-disc space-y-0.5 text-gray-600">
                  <li>{t('fertWork.maxSiresPerRun', 'Max 8 sires per run')}</li>
                  <li>
                    {t('fertWork.maxDonorsPerRun', 'Max 16 donors per run')}
                  </li>
                  <li>
                    {t('fertWork.matTimeWindowRule', '3-hour mat time window')}
                  </li>
                  <li>
                    {t('fertWork.splitThreshold', '40+ oocytes triggers split')}
                  </li>
                </ul>
              </div>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      <div data-tour="donor-grid">
        <ValidationGrid
          rowData={donorData}
          onEdit={handleEditDonor}
          onRemove={handleRemoveDonor}
          onCellValueChanged={handleCellValueChanged}
          hasWritePermission={hasWritePermission}
        />
      </div>

      <FixIssuesModal
        visible={showFixIssuesModal}
        onHide={() => setShowFixIssuesModal(false)}
        sessionId={sessionId}
        sessionDetails={sessionDetail?.details || []}
        issues={[
          ...issues.filter((issue: any) => selectedIssues.includes(issue.id)),
          ...warnings.filter((warning: any) =>
            selectedWarnings.includes(warning.id),
          ),
        ]}
      />

      <EditDonorModal
        visible={showEditModal}
        donorRecord={selectedDonorRecord}
        sessionDetails={sessionDetail?.details || []}
        aspirationDate={sessionDetail?.session?.aspirationDate}
        fertilizationSiteId={sessionDetail?.session?.siteId}
        onHide={() => {
          setShowEditModal(false);
          setSelectedDonorRecord(null);
        }}
        onSave={handleSaveDonor}
      />

      <RemoveDonorModal
        visible={showRemoveModal}
        donorRecord={selectedDonorRecord}
        onHide={() => {
          setShowRemoveModal(false);
          setSelectedDonorRecord(null);
        }}
        onConfirm={handleConfirmRemove}
      />

      <ViewDonorModal
        visible={showViewModal}
        donorRecord={selectedDonorRecord}
        onHide={() => {
          setShowViewModal(false);
          setSelectedDonorRecord(null);
        }}
      />
    </div>
  );
};
